<template>
    <div class="left-panel">
        <!-- 图纸列表区域 - 固定高度滚动 -->
        <div class="drawing-area">
            <div class="panel-section">
                <div class="section-title">
                    <div class="title-main">图纸列表</div>
                    <div class="title-sub">共 {{ totalFiles }} 个文件，{{ totalDrawings }} 张图纸</div>
                </div>

                <!-- 图纸分组折叠面板 -->
                <template v-if="!compareMode">
                    <div class="group-list">
                        <template v-for="group in drawingGroups">
                            <div :key="group.id" class="group-item">
                                <div class="group-header" :class="{ active: activeGroup === group.id }" @click="toggleGroup(group.id); $emit('select-group', group)">
                                    <span class="folder-icon">📁</span>
                                    <span class="group-name">{{ group.name }}</span>
                                    <span class="arrow-icon" :class="{ open: expandedGroups.includes(group.id) }">▼</span>
                                </div>
                                <div class="group-content" v-show="expandedGroups.includes(group.id)">
                                    <div
                                            v-for="drawing in group.drawings"
                                            :key="drawing.id"
                                            class="drawing-card"
                                            :class="{ active: activeDrawing === drawing.id }"
                                            @click="$emit('select', drawing)"
                                    >
                                        <div class="card-icon">
                                            <img src="https://cdn-icons-png.flaticon.com/512/681/681646.png" alt="图纸图标" />
                                        </div>
                                        <div class="card-info">
                                            <div class="card-name">{{ drawing.name }}</div>
                                            <div class="card-desc">{{ drawing.desc || '-' }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                </template>
                <template v-else>
                    <div class="group-list">
                        <div class="version-section-label">新版图纸</div>
                        <template v-for="group in latestGroups">
                            <div :key="group.id" class="group-item">
                                <div class="group-header" :class="{ active: activeGroup === group.id }" @click="toggleGroup(group.id); $emit('select-group', group)">
                                    <span class="folder-icon">📁</span>
                                    <span class="group-name">{{ group.name }}</span>
                                    <span class="arrow-icon" :class="{ open: expandedGroups.includes(group.id) }">▼</span>
                                </div>
                                <div class="group-content" v-show="expandedGroups.includes(group.id)">
                                    <div
                                            v-for="drawing in group.drawings"
                                            :key="drawing.id"
                                            class="drawing-card"
                                            :class="{ active: activeDrawing === drawing.id }"
                                            @click="$emit('select', drawing)"
                                    >
                                        <div class="card-icon">
                                            <img src="https://cdn-icons-png.flaticon.com/512/681/681646.png" alt="图纸图标" />
                                        </div>
                                        <div class="card-info">
                                            <div class="card-name">{{ drawing.name }}</div>
                                            <div class="card-desc">{{ drawing.desc || '-' }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                    <div class="group-list">
                        <div class="version-section-label">旧版图纸</div>
                        <template v-for="group in prevGroups">
                            <div :key="group.id" class="group-item">
                                <div class="group-header" :class="{ active: activeGroup === group.id }" @click="toggleGroup(group.id); $emit('select-group', group)">
                                    <span class="folder-icon">📁</span>
                                    <span class="group-name">{{ group.name }}</span>
                                    <span class="arrow-icon" :class="{ open: expandedGroups.includes(group.id) }">▼</span>
                                </div>
                                <div class="group-content" v-show="expandedGroups.includes(group.id)">
                                    <div
                                            v-for="drawing in group.drawings"
                                            :key="drawing.id"
                                            class="drawing-card"
                                            :class="{ active: activePrevDrawing === drawing.id }"
                                            @click="$emit('select-prev', drawing)"
                                    >
                                        <div class="card-icon">
                                            <img src="https://cdn-icons-png.flaticon.com/512/681/681646.png" alt="图纸图标" />
                                        </div>
                                        <div class="card-info">
                                            <div class="card-name">{{ drawing.name }}</div>
                                            <div class="card-desc">{{ drawing.desc || '-' }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                </template>
            </div>
        </div>

        <!-- 分割线 -->
        <div class="divider"></div>

        <!-- 版本管理区域 - 固定在底部 -->
        <div class="version-area">
            <div class="panel-section">
                <div class="section-title">
                    <div class="title-main">版本管理</div>
                </div>

                <!-- 当前版本 -->
                <div class="current-version-card">
                    <div class="version-header">
                        <div class="version-label">当前版本 <span class="version-tag">{{ currentVersion.version }}</span></div>
                        <div class="version-time">{{ currentVersion.time }}</div>
                    </div>
                    <button class="compare-btn" v-if="!viewMode && allHistoryVersions.length > 0" :disabled="!compareMode && !activeVersion" @click="$emit('compare')">{{ compareMode ? '退出对比模式' : '对比上一版本' }}</button>
                </div>

                <!-- 历史版本列表 -->
                <div class="history-version-list">
                    <div
                            v-for="v in allHistoryVersions"
                            :key="v.id"
                            class="history-version-item"
                            :class="{ active: activeVersion === v.id }"
                            @click="!viewMode && $emit('select-version', v)"
                    >
                        <div class="version-icon"></div>
                        <div class="version-text">
                            <div class="version-name">{{ v.version }}</div>
                            <div class="version-time">{{ v.time || '' }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "annotationLeftPanel",
    props: {
        drawingGroups: { type: Array, default: () => [] },
        activeDrawing: { type: [String, Number], default: null },
        currentVersion: { type: Object, default: () => ({ version: "", time: "" }) },
        historyVersions: { type: Array, default: () => [] },
        activeVersion: { type: [String, Number], default: null },
        compareMode: { type: Boolean, default: false },
        activePrevDrawing: { type: [String, Number], default: null },
        viewMode: { type: Boolean, default: false },
        auditId: { type: [String, Number], default: null },
        versionNos: { type: Array, default: () => [] },
        activeGroup: { type: [String, Number], default: null },
    },
    data() {
        return {
            expandedGroups: [],
        };
    },
    computed: {
        totalFiles() {
            return this.drawingGroups.length;
        },
        totalDrawings() {
            return this.drawingGroups.reduce((sum, g) => sum + g.drawings.length, 0);
        },
        latestGroups() {
            return this.drawingGroups.filter((g) => !g._isPrev);
        },
        prevGroups() {
            return this.drawingGroups.filter((g) => g._isPrev);
        },
        allHistoryVersions() {
            return this.historyVersions;
        },
    },
    watch: {
        drawingGroups: {
            immediate: true,
            handler(val) {
                this.expandedGroups = val.map((g) => g.id);
            },
        },
    },
    methods: {
        toggleGroup(groupId) {
            const idx = this.expandedGroups.indexOf(groupId);
            if (idx > -1) {
                this.expandedGroups.splice(idx, 1);
            } else {
                this.expandedGroups.push(groupId);
            }
        },
    },
};
</script>

<style lang="less" scoped>
.left-panel {
  width: 280px;
  min-width: 280px;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-right: 1px solid #e8e8e8;
  overflow: hidden;
}

.drawing-area {
  flex: 1;
  overflow: hidden;
  min-height: 0;
  display: flex;
  flex-direction: column;
  .panel-section {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
}
.group-list {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}
.group-list + .group-list {
  border-top: 1px solid #e8e8e8;
}
.group-list .version-section-label {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fff;
  font-size: 13px;
  font-weight: 600;
  color: #409eff;
  padding: 8px 4px 4px;
  border-bottom: 1px solid #e8e8e8;
  margin-bottom: 8px;
}
.group-item {
  margin-bottom: 12px;
  .group-header {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #fff;
    display: flex;
    align-items: center;
    padding: 12px 8px;
    cursor: pointer;
    border-radius: 8px;
    transition: background 0.2s;
    &:hover {
      background: #f5f7fa;
    }
    &.active {
      background: #eaf4ff;
      border-left: 3px solid #409eff;
      padding-left: 5px;
    }
    .folder-icon {
      font-size: 16px;
      margin-right: 8px;
    }
    .group-name {
      flex: 1;
      font-size: 16px;
      font-weight: 500;
      color: #1a1a1a;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .arrow-icon {
      font-size: 14px;
      color: #666;
      transition: transform 0.3s;
      &.open {
        transform: rotate(180deg);
      }
    }
  }
  .group-content {
    padding-left: 8px;
  }
}

.version-area {
  overflow: hidden;
}
.panel-section {
  padding: 10px 16px;
}
.section-title {
  margin-bottom: 10px;
  .title-main {
    font-size: 18px;
    font-weight: 600;
    color: #1a1a1a;
    margin-bottom: 4px;
  }
  .title-sub {
    font-size: 16px;
    color: #666666;
  }
}

// 图纸卡片样式
.drawing-card {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  margin-bottom: 10px;
  background: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  }
  &.active {
    background: #eaf4ff;
    border-color: #409eff;
  }
  .card-icon {
    width: 40px;
    height: 40px;
    margin-right: 16px;
    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }
  .card-info {
    flex: 1;
    .card-name {
      font-size: 14px;
      font-weight: 500;
      color: #1a1a1a;
      margin-bottom: 4px;
    }
    .card-desc {
      font-size: 12px;
      color: #666666;
    }
  }
}

.divider {
  height: 1px;
  background: #e8e8e8;
  margin: 0 16px;
}

// 版本管理样式
.current-version-card {
  padding: 10px;
  background: #fafafa;
  border-radius: 12px;
  margin-bottom: 10px;
  .version-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    .version-label {
      font-size: 16px;
      font-weight: 500;
      color: #1a1a1a;
      .version-tag {
        display: inline-block;
        margin-left: 8px;
        font-size: 14px;
        color: #409eff;
      }
    }
    .version-time {
      font-size: 14px;
      color: #666;
    }
  }
  .compare-btn {
    width: 100%;
    padding: 8px;
    background: #409eff;
    color: #fff;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    cursor: pointer;
    &:hover {
      background: #66b1ff;
    }
    &:disabled {
      background: #c0c4cc;
      cursor: not-allowed;
    }
  }
}

.history-version-list {
  max-height: 200px;
  overflow-y: auto;
  .history-version-item {
    display: flex;
    align-items: center;
    padding: 16px;
    margin-bottom: 12px;
    background: #ffffff;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    &:hover {
      background: #f5f7fa;
    }
    &.active {
      border-color: #409eff;
      background: #eaf4ff;
    }
    .version-icon {
      width: 40px;
      height: 40px;
      background: #f0f0f0;
      border-radius: 4px;
      margin-right: 12px;
    }
    .version-text {
      .version-name {
        font-size: 14px;
        font-weight: 500;
        color: #1a1a1a;
        margin-bottom: 4px;
      }
      .version-time {
        font-size: 12px;
        color: #666;
      }
    }
  }
}
</style>
